import { isDefined, isNotDefined } from '@sgme/fp';
import { HStack, Spacer, VStack } from '@sgme/ui';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { sendHelpRequestThunk } from '@/api/help-request/index.ts';
import type { SendHelpRequestRequest } from '@/api/help-request/sendHelpRequest.ts';
import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import {
  useCallOrderIsChecking,
  useCallOrderIsModifyingField,
  useCallOrderTileId,
} from '@/App/tiles/orders/CallOrder/CallOrderCreationTile/CallOrderContext.tsx';
import { CallOrderEditingLimitPrice } from '@/App/tiles/orders/CallOrder/CallOrderEditingTile/CallEditingLimitPrice.tsx';
import ActionButtonsForEditOrders from '@/App/tiles/orders/components/ActionButtonsForEditOrders.tsx';
import { ExecutedTileField } from '@/App/tiles/orders/components/ExecutedTileField.tsx';
import { ProgressBar } from '@/App/tiles/orders/components/ProgressBar.tsx';
import { Summary } from '@/App/tiles/orders/components/Summary.tsx';
import { ExecutionPendingOrderStatus } from '@/App/tiles/orders/ExecutionPendingOrderStatus.tsx';
import { useAccount } from '@/App/tiles/orders/hooks.tsx';
import { getStatusClass, mapStatusToColor } from '@/App/tiles/orders/utils.ts';
import { AutoAdvanceForm } from '@/components/AutoAdvanceForm.tsx';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { getConfig } from '@/config/config.ts';
import { checkIfUserCanTrade, useUserContext } from '@/context/UserContext.tsx';
import { finalStatuses, type FixingPriceType } from '@/store/bulkOrder/state.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { useUpdateCallOrderMutation } from '@/store/orders/call/api/index.ts';
import {
  selectCallOrderTile,
  selectOrderEntryByIdFromBlotter,
  selectTradeIdFromCallOrderTile,
} from '@/store/orders/call/selectors.ts';
import { ordersSlice } from '@/store/orders/slice.ts';
import type { ValidationResponseDetailContent } from '@/store/orders/state.ts';
import { removeTile } from '@/store/tiles/slice.ts';

export const CallOrderEditingTile = () => {
  const track = useTrackEvent();

  const tileId = useCallOrderTileId();
  const isChecking = useCallOrderIsChecking();
  const isModifyingField = useCallOrderIsModifyingField();

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const userContextValue = useUserContext();

  const [limitPrice, setLimitPrice] = useState<number | undefined>(undefined);

  const [updateCallOrder] = useUpdateCallOrderMutation({
    selectFromResult: () => ({}),
  });

  const tradeId = useAppSelector((state) => selectTradeIdFromCallOrderTile(state, tileId));
  const tile = useAppSelector((state) => selectCallOrderTile(state, tileId));

  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );

  const canTrade = checkIfUserCanTrade(userContextValue);

  const isDisabled = isNotDefined(tile) || !canTrade || isModifyingField;

  const account = useAccount(orderFromBlotter);

  const isFinal = finalStatuses.includes(orderFromBlotter?.status ?? '');
  const color = mapStatusToColor(orderFromBlotter?.status);

  function handleClose() {
    track('Order action', 'Remove Order');
    dispatch(removeTile(tileId));
  }

  function cancelChange() {
    track('Order action', 'Cancel editing Order');
    dispatch(ordersSlice.actions.changeOrderMode({ mode: 'VIEW', tileId }));
  }

  function amendChanges() {
    track('Order action', 'Amend Order');
    if (isNotDefined(orderFromBlotter)) {
      console.error(`[amendChanges] Tile with id ${tileId} not found in state.`);
      return;
    }

    const [currency1, currency2] = isDefined(orderFromBlotter?.currencyPair)
      ? orderFromBlotter.currencyPair.split('/')
      : ['', ''];

    if (isNotDefined(currency1) || isNotDefined(currency2) || isNotDefined(tradeId)) {
      console.error(`[amendChanges] Invalid currency pair: ${currency1}/${currency2}`);
      return;
    }

    updateCallOrder({
      tileId,
      currency1,
      currency2,
      limitPrice: isDefined(limitPrice) ? limitPrice : orderFromBlotter.limitPrice,
      orderRef: tradeId,
      startDateUtc: orderFromBlotter.startTime,
      endDateUtc: orderFromBlotter.endTime,
    })
      .unwrap()
      .then(() => {
        track('Order action', 'Amend Order successful');
        dispatch(ordersSlice.actions.changeOrderMode({ mode: 'VIEW', tileId }));
      })
      .catch(({ errors }: { type: 'failure'; errors: ValidationResponseDetailContent[] }) => {
        console.error('Failed to submit callOrder:', errors);
        if (errors.some((error) => error.errorDescription === 'timeout')) {
          const params: SendHelpRequestRequest = {
            config: 'orderpending',
            details: JSON.stringify({
              payload: errors,
              orderIdToUpdate: tileId,
            }),
            data: {
              orderDescription: 'creation',
              orderType: 'limit',
              orderCurrencyType: 'unknown',
              orderId: tileId,
            },
            templateId: getConfig().app.mailTemplateIds.templateIdForPendingOrder,
            templateIdConfirmation: getConfig().app.mailTemplateIds.templateIdForPendingOrderConfirmation,
          };

          logger.logError('Order submission failed on timeout for {tileId_s}', tileId);
          dispatch(sendHelpRequestThunk(params));
        }
      });
  }

  if (isNotDefined(orderFromBlotter)) {
    console.error(`[CallOrderEditingTile] Tile with id ${tileId} not found in blotter.`);
    return <ExecutionPendingOrderStatus />;
  }

  return (
    <AutoAdvanceForm className="bg-lvl1 w-100 h-100">
      <VStack className="bg-lvl1" verticalAlignment="start" fullWidth fullHeight>
        <HStack className="m-2">
          <div>
            <span className="fw-bold">Order {tradeId} </span>
            <span className={getStatusClass(orderFromBlotter?.status)}>{orderFromBlotter?.status}</span>
          </div>
          <button type="button" className="btn btn-flat-primary" onClick={handleClose} data-e2e="close-tile">
            <em className="icon icon-sm">close</em>
          </button>
        </HStack>

        <div className={`bg-${color} bg-opacity-10 text-${color} p-3 my-3`}>
          <ProgressBar
            executedAmount={orderFromBlotter?.executedAmount}
            notional={orderFromBlotter?.notional}
            color={color}
            isFinal={isFinal}
          />

          <div className="my-3">
            <span className="summary-text">
              <Summary
                account={account}
                orderType={orderFromBlotter?.orderType ?? 'Call'}
                way={orderFromBlotter?.yourWay ?? null}
                isGtc={orderFromBlotter?.isGtc ?? false}
                notional={orderFromBlotter?.notional ?? null}
                currency={orderFromBlotter?.notionalCurrency ?? null}
                limitPrice={limitPrice ?? orderFromBlotter?.limitPrice ?? null}
                expiryDay={orderFromBlotter?.expiryDay ?? null}
                expiryTime={orderFromBlotter?.expiryTime ?? null}
                currencyPair={orderFromBlotter?.currencyPair ?? null}
                fixingPlaceOrTime={orderFromBlotter?.fixingTime ?? orderFromBlotter?.fixingPlace ?? null}
                fixingDate={orderFromBlotter?.fixingDateUtc ?? null}
                fixingRef={orderFromBlotter?.fixingBenchmark ?? null}
                priceType={(orderFromBlotter?.fixingPriceType as FixingPriceType) ?? null}
                finalStatus={isFinal}
              />
            </span>
          </div>
        </div>

        <div>
          <ExecutedTileField label={'currencyPair'} value={orderFromBlotter?.currencyPair} />
          <ExecutedTileField label={'orderType'} value={orderFromBlotter?.orderType} />
          <ExecutedTileField
            isLast
            label={'expiry'}
            value={
              orderFromBlotter?.isGtc
                ? formatMessage({ id: 'expirationDate.GTC' })
                : `${orderFromBlotter?.expiryDay} ${orderFromBlotter?.expiryTime}`
            }
          />
        </div>

        <span className="text-muted m-8px">
          <FormattedMessage id="order.result.editableAttributes" />
        </span>
        <div
          className="mx-16px gap-8px"
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto minmax(0, 1fr)',
            gridTemplateRows: '20px',
            alignItems: 'center',
          }}
        >
          <CallOrderEditingLimitPrice limitPrice={limitPrice} setLimitPrice={setLimitPrice} />
        </div>
        <Spacer />
        <ActionButtonsForEditOrders cancelChange={cancelChange} amendChanges={amendChanges} disabled={isDisabled} />
      </VStack>
      {isChecking && (
        <div className="position-absolute z-3 top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-lvl1 bg-opacity-75">
          <div className="spinner blinker-lg" />
        </div>
      )}
    </AutoAdvanceForm>
  );
};
